// src/components/dashboard/GaugeDashboard.tsx

import { useEffect, useRef, useState } from "react";
import RGL, { WidthProvider, Layout } from "react-grid-layout";
import DashboardCard from "components/dashboard/DashboardCard";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import http from "services/httpClient";

const ReactGridLayout = WidthProvider(RGL);

interface GaugeDashboardProps {
  gauges: any[];
  onDeleteGraph: (graphId: number) => void;
  deviceId: number;
  clientRole: string | null;
}

export default function GaugeDashboard({
  gauges,
  onDeleteGraph,
  deviceId,
  clientRole
}: GaugeDashboardProps) {
  const [rowHeight, setRowHeight] = useState(240);
  const [layout, setLayout] = useState<Layout[]>([]);
  const [hasLoadedLayout, setHasLoadedLayout] = useState(false);
  const justLoadedRef = useRef(false);

  // Helper to create a default layout with 6 columns
  // const createDefaultLayout = () =>
  //   gauges.map((_, idx) => ({
  //     i: gauges[idx].id.toString(),
  //     x: idx % 6, // ensures items are spread out in 6 columns
  //     y: Math.floor(idx / 8),
  //     w: 1,
  //     h: 1,
  //   }));

  // Load layout from server or generate default
  useEffect(() => {
    if (!deviceId) return;

    const loadLayout = async () => {
      try {
        const response = await http.get(`/device/${deviceId}/layout`);
        const savedLayout = response.data;
        setLayout(savedLayout);
      } catch (error) {
        // const defaultLayout = createDefaultLayout();
        // setLayout(defaultLayout);
      }
      setHasLoadedLayout(true); // Mark layout as "loaded"
      justLoadedRef.current = true;
    };
    loadLayout();
  }, [deviceId]);

  const handleLayoutChange = (newLayout: Layout[]) => {
    // Guard 1: ignore calls before the saved layout has loaded
    // if (!hasLoadedLayout) return;
    // // Guard 2: skip the very first reflow that WidthProvider fires after load —
    // // it can measure width=0 before the browser has painted, placing every item
    // // at x:0 and overwriting the correctly loaded savedLayout.
    // if (justLoadedRef.current) {
    //   justLoadedRef.current = false;
    //   return;
    // }
    setLayout(newLayout);
  };

  // const handleLayoutChange = async (newLayout: Layout[]) => {
  //   // Avoid saving immediately after load (e.g. layout reflow)
  //   if (!hasLoadedLayout) return;

  //   if (justLoadedRef.current) {
  //     justLoadedRef.current = false;
  //     return;
  //   }

  //   setLayout(newLayout);
  //   const payload = newLayout.map(({ i, x, y, w, h }) => ({
  //     i,
  //     x,
  //     y,
  //     w,
  //     h,
  //   }));
  //   // console.log("DEBUG: Payload to save:", payload);

  //   try {
  //     await http.post(`/device/${deviceId}/layout`, payload)
  //   } catch (error) {
  //     // console.error("Error saving layout:", error);
  //   }
  // };

  const handleLayoutCommit = async (finalLayout: Layout[]) => {
    if (!hasLoadedLayout) return;                // don’t save before initial load
    if (!finalLayout || finalLayout.length === 0) return;

    const payload = finalLayout.map(({ i, x, y, w, h }) => ({
      i,
      x,
      y,
      w,
      h,
    }));

    try {
      await http.post(`/device/${deviceId}/layout`, payload);
    } catch (error) {
      // console.error("Error saving layout:", error);
    }
  };


  useEffect(() => {
    // Set a fixed height per row, regardless of resolution
    setRowHeight(220); // or 200, 180, depending on how tall you want a row
  }, []);

  const handleDuplicate = async (config: any) => {
    try {
      const newGraphPayload = {
        device_id: deviceId,
        graph_config: {
          ...config,
          id: undefined,
          title: (config.title || "Gauge") + " (Copy)",
          chart_labels: config.chart_labels || config.graph_config?.chart_labels || [],
        },
      };

      const res = await http.post(`/graphs`, newGraphPayload);
      const newGraph = res.data;

      const maxY = layout.reduce((max, item) => Math.max(max, item.y + item.h), 0);

      const newLayoutItem = {
        i: newGraph.id.toString(),
        x: 0,
        y: maxY,
        w: 1,
        h: 1,
      };

      const updatedLayout = [...layout, newLayoutItem];
      const payload = updatedLayout.map(({ i, x, y, w, h }) => ({ i, x, y, w, h }));

      await http.post(`/device/${deviceId}/layout`, payload);
      setLayout(updatedLayout);
      window.location.reload();
    } catch (err) {
      // console.error("❌ Failed to duplicate gauge:", err);
    }
  };

  if (!hasLoadedLayout && gauges.length > 0) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "300px" }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading layout...</span>
        </div>
      </div>
    );
  }

  return (
    <div style={{ width: "100%" }}>
      <ReactGridLayout
        className="layout"
        layout={layout}
        cols={6}
        rowHeight={rowHeight}
        draggableHandle=".drag-handle"
        draggableCancel=".no-drag"
        useCSSTransforms={true}
        autoSize={true}
        margin={[5, 5]}             
        containerPadding={[5, 5]}
        // preventCollision={true}         
        compactType="vertical"           
        isDroppable={false} 
        onLayoutChange={handleLayoutChange}
        onDragStop={handleLayoutCommit}
        onResizeStop={handleLayoutCommit}
      >
        {gauges.map((gauge) => (
          <div key={gauge.id} data-grid={layout.find(l => l.i === gauge.id.toString())}>
          {/* <div key={gauge.id}> */}
            <DashboardCard
              config={{
                ...gauge.graph_config,
                id: gauge.id,
                device_id: gauge.device_id,
              }}
              onDelete={() => onDeleteGraph(gauge.id)}
              onDuplicate={(config) => handleDuplicate(config)}
              clientRole={clientRole} 
            />
          </div>
        ))}
      </ReactGridLayout>
    </div>
  );
}
